// src/components/desk/CooperationModal.jsx — the cooperation open/amend modal (agency↔
// listing-contact relationship, core/migrations/0046) — extracted VERBATIM from IntakeTab.jsx
// (DESK_MODE_REWRITE_PLAN §3 decomposition, behavior-preserving). Rendered by PersonModal,
// the lineage modal and IntakeTab's top level; carries no layout responsibility.

import { useEffect, useRef, useState } from 'react'
import { t } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { card, input, btn, Modal } from './primitives.jsx'
import { COOP_STATUS_KEYS } from './CooperationChips.jsx'
import { amendCooperation, getCooperation, openCooperation, setCooperationStatus, updateCooperationParticipants } from '../../db/core.js'
import { commissionAmount as sharedCommissionAmount } from '../../lib/coopMath.js'
import { convertMoney } from '../../lib/money.js'
import { useUsdGelRate } from '../../lib/fx.js'
import { parseMoneyInput } from '../../lib/moneyInput.js'
// The roster, and the same active-agent filter every other picker uses — a resigned
// colleague must not be offerable as a new participant on a live deal.
import { DateInput } from '../SegmentedInput.jsx'
import { useRoster, isActiveAgent } from './CobrokePanel.jsx'

// ─── Cooperation modal — the agency↔listing-contact relationship (core/migrations/0046) ───
// Identity = property + contact + intent, never the terms — reopening an already-active
// cooperation on this property/contact just loads it (existed:true), never overwrites terms.
// The commission/price cross-check is a UI convenience ONLY: commission_percent and min_price
// are the two stable columns that actually get written; the derived commission amount shown
// below is recomputed on every render, never persisted (see core/migrations/0046 header —
// "calculator = suggestion layer only, never authoritative output").
// coopMeetingSeed / COOP_STATUS_KEYS / COOP_INTENT_KEYS / coopIntentOf — moved to ./desk/CooperationChips.jsx (plan §3 decomposition).
// listingId (2026-07-28): the ad this cooperation is being opened FROM, recorded as
// evidence in cooperation_listing. Optional — the Person view opens this modal with no
// listing in scope, and a cooperation is identified by property + contact + intent, never
// by an ad (0046: "discovery is not identity").
// ── Adding a colleague to this cooperation (owner ruling 2026-08-15) ────────────────────
// This replaces a text box that asked for a party UUID. Nobody has ever known one, the Desk
// displays none anywhere to copy, and the field's own comment admitted it was "wrong for
// agents" — it survived because deleting it would have removed the only way to add anyone
// while the workflow question was open. The owner closed both halves at once: the roster,
// and management only.
//
// The roster, not a search over parties: a participant here is a COLLEAGUE by definition
// (an agent at another agency is the co-broke split's business, not this list), and an
// office roster is short enough to read. Same shape as the meeting's guest picker, for the
// same reason — one convention for "pick one of our agents".
// Anyone already on the deal is filtered out rather than shown greyed: the question is "who
// else", and offering a name that does nothing is a control that lies.
function AddParticipantPicker({ taken = [], busy, onPick }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const roster = useRoster()
  const ql = q.trim().toLowerCase()
  // `taken` holds PARTY ids and the roster holds AGENT ids, so a name match is the only
  // join available on the client — deliberately, because the bridge belongs to the server.
  // A near-miss here costs a duplicate the gateway refuses, never a wrong write.
  const takenNames = new Set(taken.map(String))
  const free = roster.filter(a => isActiveAgent(a)
    && !takenNames.has(String(a.party_id || ''))
    && (!ql || (a.name || '').toLowerCase().includes(ql)))
  if (!open) {
    return (
      <button type="button" className={btn('ghost')} disabled={busy} onClick={() => setOpen(true)}>
        {t('desk_coop_add_participant')}
      </button>
    )
  }
  return (
    <div className="rounded-lg border border-border bg-background/60 px-2 py-2 space-y-1">
      <input className={input} autoFocus placeholder={t('meeting_guest_search_ph')}
        value={q} onChange={e => setQ(e.target.value)} />
      <div className="max-h-40 overflow-y-auto space-y-0.5">
        {free.length === 0
          ? <p className="text-xs text-muted-foreground px-1 py-1">{t('meeting_property_search_none')}</p>
          : free.slice(0, 40).map(a => (
              <button key={a.id} type="button" disabled={busy}
                onClick={() => { setOpen(false); setQ(''); onPick(a) }}
                className="w-full text-left px-2 py-1 rounded-md hover:bg-accent text-sm truncate disabled:opacity-50">
                {a.name}
              </button>
            ))}
      </div>
      <div className="flex justify-end">
        <button type="button" className="text-xs text-muted-foreground"
          onClick={() => { setOpen(false); setQ('') }}>{t('cancel')}</button>
      </div>
    </div>
  )
}

// seedCommission (B868, owner design (b), 2026-08-20): the OWNER'S OWN stated split from the
// ad ("ვთანამშრომლობ აგენტებთან 50%"), prefilled into a NEW cooperation's commission field.
// A prefill, never a fact: the agent confirms by saving, and a LOADED cooperation's stored
// value always overrides it (the load path setForm's src wins). Rides the baseline like the
// inherited price, so an untouched prefill never reads as an unsaved edit.
// manualSave (owner-reported 2026-09-08): AUTOSAVE IS WRONG WHEN THIS FORM IS A SECTION OF A
// BIGGER ONE. Inside "new meeting" the host collapses this panel in `onSaved` — correct for a
// deliberate save, fatal for an automatic one: typing the first commission field fired the
// 700 ms autosave, which fired onSaved, which closed the panel under the agent before they
// could reach the percent box. The autosave is right where this form IS the task (the deal
// editor, the lineage modal); it is wrong where it is one block of a form the agent is still
// filling in, and the difference is the host's, so the host declares it.
function CooperationModal({ propertyId, propertyLabel, contactPartyId, contactName, cooperationId, onClose, onSaved, seedIntent = null, seedCurrency = null, seedPrice = null, seedCommission = null, seedCommissionType = null, listingId = null, inline = false, scopeSelfOnly = false, manualSave = false, onStateChange = null, registerApi = null }) {
  const [loading, setLoading]   = useState(!!cooperationId)
  const [saving, setSaving]     = useState(false)
  const [autoSaveState, setAutoSaveState] = useState('idle')
  const failedAutoSaveRef = useRef(null)
  const [err, setErr]           = useState(null)
  const [id, setId]             = useState(cooperationId || null)
  const [orig, setOrig]         = useState(null) // last-saved snapshot, for diffing amend ops
  const [participants, setParticipants] = useState([])
  // Server's answer to "what may THIS viewer see and do" (core_visibility.js
  // projectCooperation → visibility{}). The modal renders this and never re-derives the
  // rule: a client that re-implements authorization is another parallel gate, exactly what
  // B58 collapsed on the read side. Authoritative check stays coopAuthority (403);
  // this only decides what to draw. null until loaded, or when the server predates the
  // field — every read below falls back to the old behaviour, never to something wider.
  const [vis, setVis] = useState(null)
  // per-agent terms scope (0122): whose participant terms the form is editing.
  // null = parent/shared (legacy admin view). Defaulted to the single active
  // participant on load — the common case. The parent's own term columns are a
  // ROLLED-UP effective view maintained by the gateway; each agent's real
  // negotiation lives on their cooperation_participant row.
  const [editingPartyId, setEditingPartyId] = useState(null)
  const [origP, setOrigP] = useState(null) // selected participant's last-saved snapshot

  // seedIntent/seedCurrency: prefilled from the LISTING this modal was opened on (new
  // cooperations only — loading an existing one overwrites from the stored row).
  //
  // ── THE THREE PRICES (owner ruling 2026-08-01) ──────────────────────────────────
  // ad price (owner's, from the scrape)  →  agreed price (this field)  →  repost price
  // Each INHERITS the one before it until a hand-fills it; from then on it is that
  // agent's own value and no upstream change may overwrite it. seedPrice is link 1.
  //
  // `priceInherited` = "this number came from the ad and the agent has not retyped it".
  // ⚠️ It is NOT a write gate. An earlier version withheld inherited values from the DB
  // until confirmed; the owner overruled that on 2026-08-01 ("no need to be accepted, let
  // it be there"), so an inherited price saves like any other value. The flag now drives
  // exactly two things, and nothing else should be hung on it:
  //   1. the hover that says where the number came from (provenance on demand, not a line
  //      of explanation on every open), and
  //   2. the currency rule below — inherited converts, hand-typed does not.
  const [intent, setIntent]         = useState(seedIntent || 'sale')
  const [status, setStatus]         = useState('proposed')
  const [endReasonOpen, setEndReasonOpen] = useState(false)   // B115 v2: ask WHY before ending
  const [endReason, setEndReason]         = useState(null)
  const [endNote, setEndNote]             = useState('')       // mandatory for 'unknown'
  const [exclUntil, setExclUntil]         = useState('')       // date | 'until_sold'
  // Commission shape (2026-07-12, agent-directed): sale has 3 mutually-exclusive shapes
  // (percent of price / owner's net ask, agent keeps the spread / flat fee regardless of
  // price); rent has its own 3 (percent of one month / flat fee / "standard" — a KNOWN
  // formula, no fields to fill: 100% of one month for a 12-month lease, pro-rated for
  // shorter terms). Stored in cooperation.terms jsonb (commission_type / owner_net_amount /
  // net_commission_amount) — verified amend_cooperation accepts any terms.<key> path, no
  // migration needed. commission_percent stays a real column but is only meaningful (and
  // only written) when commissionType === 'percent'.
  // 🔴 THE SEED CARRIES ITS TYPE (2026-09-16). The owner's „50%“ is a share of the COMMISSION,
  // and it was prefilled under 'percent' — percent of the PRICE. 91 cooperations saved it that
  // way. A seeded split opens as coop_split; a stated fee on the price opens as percent.
  const seedType = seedCommission != null ? (seedCommissionType || 'coop_split') : 'percent'
  const [commissionType, setCommissionType] = useState(seedType)
  const [commissionPct, setCommissionPct] = useState(seedCommission != null ? String(seedCommission) : '')
  const [ownerNetAmount, setOwnerNetAmount] = useState('')       // sale: net_amount type
  const [netCommissionAmount, setNetCommissionAmount] = useState('') // sale: net_commission / rent: fixed_net
  const [minPrice, setMinPrice]     = useState(seedPrice != null ? String(seedPrice) : '')
  const [currency, setCurrency]     = useState(seedCurrency || 'USD')
  // true while minPrice still holds the INHERITED ad price nobody has confirmed — see
  // the ⚠️ above. Any edit or the confirm button clears it, and it is always false once
  // the cooperation itself has a stored min_price.
  const [priceInherited, setPriceInherited] = useState(seedPrice != null)
  const editPrice = v => { setMinPrice(v); setPriceInherited(false) }
  const fxRate = useUsdGelRate()
  // ⚠️ Inheriting carries the CURRENCY with the number (owner ruling 2026-08-01), so the
  // two states answer a currency switch differently:
  //   • still inherited → the amount is the AD's, and the dropdown only chooses how to
  //     express it, so convert at the NBG rate and keep the same real value.
  //   • hand-typed/confirmed → the agent stated this number; switching the unit must never
  //     rewrite their digits (they may well be typing the number first and the unit second).
  // A conversion we cannot do honestly is refused rather than faked: convertMoney returns
  // null without a real rate, and we then leave the amount and the currency alone.
  function changeCurrency(next) {
    if (priceInherited && normV(minPrice) !== '') {
      const converted = convertMoney(parseMoneyInput(minPrice), currency, next, fxRate)
      if (converted == null) return          // no rate → refuse, never relabel
      setMinPrice(String(Math.round(converted)))
    }
    setCurrency(next)
  }
  const [exclusive, setExclusive]   = useState(false)
  // 1073 — the owner's "do not copy this advert". AGENCY-level like `exclusive`: it is
  // the owner's instruction about their own listing, not one agent's negotiated term, so
  // it rides the parent door and survives a scope switch (see selectParticipant).
  const [noRepublish, setNoRepublish] = useState(false)
  // 1080 — a SECOND, independent refusal. „არ გააზიაროთ" is about the advert; „არ
  // თანამშრომლობს" is about the relationship, and an owner may publish either or both
  // (owner 2026-09-12). No exclusion between them — only exclusivity contradicts.
  const [noCooperation, setNoCooperation] = useState(false)
  const [validUntil, setValidUntil] = useState('')
  const [notes, setNotes]           = useState('')

  // ── Browser-side draft (owner request 2026-08-01: "nothing is lost") ─────────────
  // Typed-but-unsaved terms survive closing/reopening the modal via localStorage.
  // The draft exists exactly while the form is DIRTY (differs from the last-saved
  // baseline) and is deleted on save/discard — so a clean reopen never shows a
  // stale "restored" note. Values only, never functions (see memory:
  // setter-persists-updater-function); 24h TTL so week-old drafts don't resurrect.
  const draftKey = 'sntv_coop_draft:' + (cooperationId || `new:${propertyId || ''}:${contactPartyId || ''}:${seedIntent || ''}`)
  const [draftRestored, setDraftRestored] = useState(false)
  // Baseline = what the last save (or the server load) said. Dirty is measured
  // against THIS, not against empty — an untouched loaded form is not dirty.
  // ⚠️ The INHERITED price goes into the baseline too: an ad price the agent has not
  // touched must not read as an unsaved edit, or the save-guard would fire on every
  // repost and store it.
  // `priceInherited` rides along in the baseline (it is state ABOUT a field, not a field —
  // `dirty` iterates fieldsNow, so it is correctly ignored there) so that discarding a
  // draft restores the inheritance too, not just the number.
  const baseRef = useRef({
    commissionType: seedType, commissionPct: seedCommission != null ? String(seedCommission) : '',
    minPrice: seedPrice != null ? String(seedPrice) : '', ownerNetAmount: '',
    netCommissionAmount: '', currency: seedCurrency || 'USD', exclusive: false,
    noRepublish: false, noCooperation: false,
    validUntil: '', notes: '', priceInherited: seedPrice != null,
  })
  const fieldsNow = { commissionType, commissionPct, minPrice, ownerNetAmount, netCommissionAmount, currency, exclusive, noRepublish, noCooperation, validUntil, notes }
  const normV = v => v == null ? '' : String(v)
  // ⚠️ The inherited price is in the BASELINE, so simply opening a form does not count as
  // an edit. That keeps the draft store and the save-before-repost guard quiet on every
  // open; the value still saves whenever a save actually happens (see handleSave).
  const dirty = !loading && (Object.keys(fieldsNow).some(k =>
    (k === 'exclusive' || k === 'noRepublish' || k === 'noCooperation') ? !!fieldsNow[k] !== !!baseRef.current[k] : normV(fieldsNow[k]) !== normV(baseRef.current[k]))
    || (!!id && !!orig && status !== orig.status))
  const autoSaveSignature = JSON.stringify({ ...fieldsNow, status })

  function readDraft() {
    try {
      const raw = localStorage.getItem(draftKey)
      if (!raw) return null
      const d = JSON.parse(raw)
      if (!d?.fields || (Date.now() - (d.at || 0)) > 24 * 60 * 60 * 1000) { localStorage.removeItem(draftKey); return null }
      return d.fields
    } catch { return null }
  }
  function applyFields(f) {
    setCommissionType(f.commissionType ?? 'percent'); setCommissionPct(f.commissionPct ?? '')
    setMinPrice(f.minPrice ?? ''); setOwnerNetAmount(f.ownerNetAmount ?? ''); setNetCommissionAmount(f.netCommissionAmount ?? '')
    setCurrency(f.currency ?? 'USD'); setExclusive(!!f.exclusive); setNoRepublish(!!f.noRepublish); setNoCooperation(!!f.noCooperation)
    setValidUntil(f.validUntil ?? ''); setNotes(f.notes ?? '')
    setPriceInherited(!!f.priceInherited)
  }
  function discardDraft() {
    try { localStorage.removeItem(draftKey) } catch {}
    applyFields(baseRef.current); setDraftRestored(false)
  }
  // Persist while dirty, delete when clean — dirty IS the draft's existence condition.
  useEffect(() => {
    if (loading) return
    try {
      if (dirty) localStorage.setItem(draftKey, JSON.stringify({ at: Date.now(), fields: { ...fieldsNow, priceInherited } }))
      else localStorage.removeItem(draftKey)
    } catch {}
  })
  // New cooperation: restore a draft on mount (existing ones restore inside the load
  // effect below, AFTER the server row has set the baseline).
  useEffect(() => {
    if (cooperationId) return
    const d = readDraft()
    if (d) { applyFields({ ...baseRef.current, ...d }); setDraftRestored(true) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!cooperationId) return
    let cancelled = false
    getCooperation(cooperationId).then(d => {
      if (cancelled) return
      setId(d.id)
      setIntent(d.transaction_intent); setStatus(d.status)
      setExclusive(!!d.exclusive); setNoRepublish(!!d.no_republish); setNoCooperation(!!d.no_cooperation)
      setParticipants(d.participants || [])
      setVis(d.visibility || null)
      setOrig(d)
      // Default scope = MY OWN row (2026-07-29). Was "the single active participant, else
      // nothing", so with 2+ participants the form opened unscoped, let the agent fill it
      // in, and only then refused on save with "pick a participant" (the old
      // desk_coop_pick_participant path) — a form that accepts input it has already decided
      // it cannot save. An agent may only ever write their OWN terms
      // (COOPERATION_VISIBILITY_SPEC §7), so there is exactly one correct default and the
      // server now names it: participant.is_self.
      // ⚠️ The `actives.length === 1` fallback is deliberate, not redundant: against a
      // server that predates is_self, this restores the previous behaviour exactly. Do not
      // "simplify" it away until the projection change is deployed everywhere.
      const actives = (d.participants || []).filter(p => !p.left_at)
      // ⚠️ The single-active fallback must NOT hand a non-participant another agent's row
      // (owner-flagged 2026-07-29: a locked viewer saw "editing <colleague>'s terms").
      // It stays only for viewers the server says may amend others (management), and for
      // servers predating visibility{} (old behaviour, exactly as before).
      // ⚠️ scopeSelfOnly kills the single-active fallback entirely (2026-08-01). From a
      // MEETING there is exactly one row anybody should be touching — their own — and the
      // fallback was handing over a colleague's: Tatia opened her own meeting and the panel
      // said "you are editing Levan Papiashvili's terms". Management legitimately may amend
      // others, but not by ACCIDENT and not from a surface about somebody else's viewing;
      // they still can from the cooperation's own modal, where the participant list is the
      // subject rather than a side effect.
      const mayScopeOthers = scopeSelfOnly ? false : (d.visibility ? !!d.visibility.can_amend_others_terms : true)
      const selfRow = actives.find(p => p.is_self)
      // A manager is an agent PLUS a manager (owner ruling 2026-08-05): opening a
      // cooperation they are not yet in must offer THEIR OWN terms (join-on-save,
      // same as any non-participant agent), not silently scope them onto the one
      // active colleague's row — which read as "you may edit Giorgi's negotiation
      // but cannot record yours". Editing others stays one click away (✎ on the
      // participant row); it just stops being the default.
      const mgrJoining = !selfRow && d.visibility?.relation === 'manager' && !!d.contact_party_id
      const mine = selfRow || (!mgrJoining && actives.length === 1 && mayScopeOthers ? actives[0] : null)
      // ⚠️ Recording YOUR OWN terms starts EMPTY, never seeded from the parent. The parent's
      // numbers are the rollup of whoever confirmed last, so seeding them would have Tatia
      // save Levan's 342 as her own the moment she pressed შენახვა — the same bug one step
      // later. Only when she will actually JOIN on save, though: for a viewer amending the
      // PARENT (management), blanking the form would diff as a CLEAR and wipe the recorded
      // deal. Empty is safe exactly when the save target is a brand-new row.
      // mgrJoining rides the same rule: a manager's fresh row is the save target, so
      // seeding the parent rollup would have them save a colleague's numbers as their own.
      const joining = mgrJoining || (scopeSelfOnly && !mine
        && d.visibility?.reason === 'not_participant' && !!d.contact_party_id)
      const src = mine || (joining ? {} : d)
      setEditingPartyId(mine ? mine.party_id : null)
      setOrigP(mine || null)
      // ⚠️ Read-only load: this viewer may not amend, so the numbers on screen are somebody
      // else's record (or redacted). Inheritance is an aid to FILLING a form — seeding the
      // ad price here would put an unowned number in the box that reads exactly like a
      // colleague's agreed price. This guard matters MORE now that inherited values save
      // without a confirm step, not less. Computed before `base`, which depends on it.
      const roLoad = d.visibility?.can_amend_own_terms === false
        && !(d.visibility?.reason === 'not_participant' && d.contact_party_id)
      // A STORED price is this agent's own answer and always wins — the chain only
      // inherits into an empty field (owner ruling 2026-08-01: "when filled by hand it
      // must not change"). So re-opening never re-seeds over a recorded agreement, and
      // an agent who deliberately left it blank sees the ad price offered again.
      const inheritPrice = !roLoad && src.min_price == null && seedPrice != null
      const base = {
        commissionType: src.terms?.commission_type || 'percent',
        commissionPct: src.commission_percent ?? '',
        minPrice: inheritPrice ? String(seedPrice) : (src.min_price ?? ''),
        ownerNetAmount: src.terms?.owner_net_amount ?? '', netCommissionAmount: src.terms?.net_commission_amount ?? '',
        currency: src.min_price_currency || (inheritPrice && seedCurrency) || 'USD', exclusive: !!d.exclusive,
        noRepublish: !!d.no_republish, noCooperation: !!d.no_cooperation,
        validUntil: src.valid_until || '', notes: src.notes || '',
        priceInherited: inheritPrice,
      }
      baseRef.current = base
      applyFields(base)
      // Overlay an unsaved browser draft ON TOP of the server row — but never onto a
      // form the server says this viewer may not edit (a read-only draft would render
      // uneditable "restored changes" that can neither be saved nor typed over).
      const draft = roLoad ? null : readDraft()
      if (draft) { applyFields({ ...base, ...draft }); setDraftRestored(true) }
      setLoading(false)
      setAutoSaveState('saved')
    }).catch(e => { if (!cancelled) { setErr(e.message); setLoading(false) } })
    return () => { cancelled = true }
  }, [cooperationId])

  // pure UI helper — never stored (see header). ONE implementation for every surface
  // that projects an amount from cooperation terms (lib/coopMath.js, 2026-07-27) — this
  // preview, the Desk feed's commission sort, and by contract the extension's gallery
  // calculator all speak the same shapes.
  const commissionAmount = sharedCommissionAmount({
    type: commissionType, price: minPrice, percent: commissionPct,
    ownerNet: ownerNetAmount, net: netCommissionAmount,
  })

  // A form you cannot submit must say so BEFORE you fill it in (owner ruling 2026-07-29).
  // Previously a non-participant got a fully editable commission form, spent the time
  // typing, pressed Save — and only then learned they were never allowed to. The rejection
  // taught the rule at the most expensive possible moment. Read-only + one sentence naming
  // the ACTION that unlocks it ("join this cooperation") beats both an editable dead end
  // and a bare padlock.
  // `=== false` (not falsy): null = still loading, or a server without visibility{} — both
  // must stay editable, exactly as before. Only an explicit server "no" locks the form.
  // New cooperations (no id yet) are always editable — there is nothing to be excluded from.
  // JOIN-ON-SAVE (owner ruling 2026-07-29, replacing the same-day join button):
  // "if an agent writes in and saves, that already means they're in; if not, they
  // close the modal." So a non-participant's form stays EDITABLE, the banner says
  // saving joins you, and handleSave does the join before applying their ops.
  //
  // ⚠️ ASK `relation`, NOT `reason`. `reason` explains why NUMBERS are hidden
  // (contribute_to_reveal / not_participant) and is NULL whenever the viewer CAN see them —
  // it was only ever a working participation test by coincidence. The meeting number-unlock
  // (owner ruling 2026-08-01: an agent on a meeting sees that property's terms) broke the
  // coincidence the day it shipped: the unlock sets seeNumbers → reason null, so a
  // NON-participant fell through to readOnly and "+ შეავსე პირობები" opened a LOCKED form
  // saying "you may view this cooperation, not change it" — the feature meant to help the
  // agent walking into the viewing was the exact thing stopping her recording her own terms.
  // `relation` is the participation fact ('participant' | 'manager' | 'agency'); the `reason`
  // fallback stays only for a server predating it, where it behaved as before.
  // A manager with no row of their own is ALSO a joinable non-participant (2026-08-05:
  // "a manager acts like an agent in any instance") — their save lands on their own
  // fresh participant row, exactly like an agency agent's. `relation` alone can't say
  // this: it reads 'manager' whether or not they participate, so the participant list
  // supplies the missing fact.
  const selfActive = participants.some(p => !p.left_at && p.is_self)
  const notParticipant = vis
    ? (vis.relation
        ? vis.relation === 'agency' || (vis.relation === 'manager' && !selfActive)
        : vis.reason === 'not_participant')
    : false
  // `!editingPartyId`: a manager may scope onto a colleague's row (✎) while still not
  // participating — that save must amend THE COLLEAGUE's row, never divert onto a fresh
  // row of the manager's own. Joining happens only when the form is in own-terms scope.
  const joinsOnSave = !!id && notParticipant && !editingPartyId && !!orig?.contact_party_id
  const readOnly = !!id && vis?.can_amend_own_terms === false && !joinsOnSave

  // Report live state up to DealEditor (dirty → save-before-reshare guard; price → the
  // next link in the inheritance chain, the repost price). Value-compared against the
  // last report so the parent's setState cannot ping-pong a render loop with this effect.
  // An inherited price IS reported (2026-08-01): now that it saves like any other value,
  // it is this cooperation's price, so the repost should follow it — including after a
  // currency switch converted it, which is the one case where the two would otherwise
  // disagree (the Share tab inherits the ad's untouched pair by its own route).
  const lastReport = useRef(null)
  useEffect(() => {
    if (!onStateChange) return
    const s = {
      dirty: dirty && !readOnly, joinsOnSave,
      price: minPrice === '' || minPrice == null ? null : parseMoneyInput(minPrice),
      currency,
    }
    const p = lastReport.current
    if (!p || p.dirty !== s.dirty || p.joinsOnSave !== s.joinsOnSave || p.price !== s.price || p.currency !== s.currency) {
      lastReport.current = s
      onStateChange(s)
    }
  })
  // Hand the parent a save handle (kept fresh every render — handleSave closes over state).
  useEffect(() => { registerApi?.({ save: handleSave }) })

  // silent (2026-08-01): save without closing/fronting — used by DealEditor to flush
  // dirty terms before a reshare fires ("press on any portal button must be doing save").
  // Silent failures RETHROW so the caller can abort the reshare; the visible error is
  // still set here for when the agent returns to this tab.
  async function handleSave(opts = {}) {
    const silent = !!opts.silent
    const automatic = !!opts.automatic
    if (automatic) setAutoSaveState('saving')
    setSaving(true); setErr(null)
    try {
      // ⚠️ 0–100 is a DATABASE constraint (cooperation_participant_commission_percent_check),
      // not a house style — the amend RPC rejects anything outside it and PostgREST returns a
      // bare 400. That 400 is rethrown below so the caller ABORTS THE RESHARE, so an
      // out-of-range percent silently blocks publishing with nothing on screen to explain it:
      // on 2026-09-20 one agent hit it 25 times in three minutes. The number people type when
      // they get this wrong is the FEE (the box sits beside min_price), which is why the
      // message names the net-amount type rather than just restating the range.
      // Checked for coop_split too: it stores the same column under a different label.
      const pctNum = commissionPct === '' || commissionPct == null ? null : Number(commissionPct)
      if ((commissionType === 'percent' || commissionType === 'coop_split')
          && pctNum != null
          && (!Number.isFinite(pctNum) || pctNum < 0 || pctNum > 100)) {
        throw new Error(t('desk_coop_pct_out_of_range'))
      }
      const conditions = {
        commission_type: commissionType,
        // Owner's net ask on a SALE is a fact of every sale call, whatever the fee type (2026-09-16:
        // 0 of 64 cooperations carried it while agents typed "112 უნდა ხელზე" as comments, because
        // the box only appeared after choosing the net_amount type). Under net_amount it also
        // drives the commission; under any other type it is recorded, never computed with.
        owner_net_amount: (commissionType === 'net_amount' || intent === 'sale') ? (ownerNetAmount === '' ? null : parseMoneyInput(ownerNetAmount)) : null,
        net_commission_amount: (commissionType === 'net_commission' || commissionType === 'fixed_net')
          ? (netCommissionAmount === '' ? null : parseMoneyInput(netCommissionAmount)) : null,
      }
      const terms = {
        // ⚠️ coop_split stores its share in the SAME column (CooperationChips reads it there), and
        // until 2026-09-16 this line wrote null for it — the split type saved, its number did not.
        commission_percent: (commissionType === 'percent' || commissionType === 'coop_split') ? (commissionPct === '' ? null : parseFloat(commissionPct)) : null,
        exclusive,
        // An inherited price saves like any other value (owner ruling 2026-08-01: "no need
        // to be accepted, let it be there"). The earlier confirm-before-write step is gone.
        min_price: minPrice === '' ? null : parseMoneyInput(minPrice),
        min_price_currency: currency,
        valid_until: exclusive ? (validUntil || null) : null,
        conditions,
      }
      // existing cooperation: amend only the fields that actually changed (op-list, never a blind PATCH).
      // 0122: term ops target the selected agent's OWN participant row when a scope is
      // active (editingPartyId) — the gateway rolls the parent's effective view up.
      // exclusive (+ holder) is agency-level and ALWAYS amends the parent.
      // Join-on-save: find-or-create lands on THIS cooperation (exact triple or the
      // phone-membership reuse) and adds the caller as a participant; their ops then
      // target their own FRESH row (empty baseline), never the parent.
      let coopId = id
      let scopeParty = editingPartyId
      let before = editingPartyId ? origP : orig
      let origRow = orig
      // A fresh form never CHOSE a status or ticked-off exclusivity — its 'proposed'/false
      // are defaults, not statements. Only a form loaded from a stored row may write them,
      // or the existed-fallthrough below would silently regress a live cooperation's
      // status and revoke its exclusivity on every raced save.
      let loadedForm = !!id
      if (!coopId) {
        const r = await openCooperation({
          propertyId, contactPartyId, transactionIntent: intent,
          terms, status, notes: notes || null, listingId,
        })
        setId(r.cooperation_id)
        coopId = r.cooperation_id
        if (!r.existed) {
          // genuinely created — our terms landed with it, nothing left to amend
          try { localStorage.removeItem(draftKey) } catch {}
          baseRef.current = { ...fieldsNow, priceInherited }
          setDraftRestored(false)
          if (silent) {
            // Autosave created the relationship on the first meaningful edit. Load the
            // server projection immediately so later edits target the caller's participant
            // row rather than falling through to an unscoped parent amendment.
            const d = await getCooperation(coopId)
            const mine = (d.participants || []).filter(p => !p.left_at).find(p => p.is_self)
            setParticipants(d.participants || []); setVis(d.visibility || null); setOrig(d)
            if (mine) { setEditingPartyId(mine.party_id); setOrigP(mine) }
            if (automatic) { failedAutoSaveRef.current = null; setAutoSaveState('saved') }
            onSaved?.()
            return
          }
          onSaved?.()
          onClose()
          return
        }
        // ⚠️ existed:true — the seed raced the coops fetch (URL restore / 🤝＋ before the
        // index loaded) and this property+contact+intent already has an active cooperation.
        // open_cooperation deliberately never touches terms on reuse, so stopping here
        // would report success over a write that changed nothing (the irakli incident,
        // 2026-08-06: filled, saved, came back — blank). Load the row and push the typed
        // values through the amend path, scoped to our own participant row (which the
        // /open route just ensured exists — it defaults participants to the caller).
        const d = await getCooperation(coopId)
        const mine = (d.participants || []).filter(p => !p.left_at).find(p => p.is_self)
        setParticipants(d.participants || []); setVis(d.visibility || null); setOrig(d)
        if (mine) { setEditingPartyId(mine.party_id); setOrigP(mine) }
        scopeParty = mine ? mine.party_id : null
        before = mine || d
        origRow = d
      } else if (joinsOnSave) {
        await openCooperation({
          propertyId: orig?.property_id || propertyId,
          contactPartyId: orig.contact_party_id,
          transactionIntent: orig?.transaction_intent || intent,
        })
        const d = await getCooperation(coopId)
        const mine = (d.participants || []).filter(p => !p.left_at).find(p => p.is_self)
        if (!mine) throw new Error(t('desk_coop_join_to_record'))
        setParticipants(d.participants || []); setVis(d.visibility || null); setOrig(d)
        setEditingPartyId(mine.party_id); setOrigP(mine)
        scopeParty = mine.party_id; before = mine; origRow = d
      }
      const ops = []
      const maybe = (field, val, prev) => { if (val !== prev) ops.push({ op: val == null || val === '' ? 'clear' : 'set', field, value: val }) }
      maybe('commission_percent', terms.commission_percent, before?.commission_percent)
      maybe('min_price', terms.min_price, before?.min_price)
      // A currency with no price on either side says nothing — don't write the ad's
      // currency onto a row whose price was never recorded.
      maybe('min_price_currency',
        (terms.min_price != null || before?.min_price != null) ? currency : (before?.min_price_currency ?? null),
        before?.min_price_currency)
      maybe('valid_until', terms.valid_until, before?.valid_until)
      maybe('notes', notes || null, before?.notes)
      maybe('terms.commission_type', conditions.commission_type, before?.terms?.commission_type)
      maybe('terms.owner_net_amount', conditions.owner_net_amount, before?.terms?.owner_net_amount)
      maybe('terms.net_commission_amount', conditions.net_commission_amount, before?.terms?.net_commission_amount)
      // Terms belong to a PARTICIPATION, never to the shared parent (2026-07-20, BACKLOG
      // B67b). The parent's term columns are a rolled-up effective view the gateway
      // derives; writing them directly was the other half of the drift — the parent door
      // never syncs participants, so the two rows ended up disagreeing. With one active
      // participant this is already scoped automatically (the common case, 633/642); with
      // several, "whose 500 is this?" has no default answer, so ask rather than guess.
      if (ops.length && !scopeParty) {
        setErr(t('desk_coop_pick_participant')); setSaving(false); return
      }
      if (ops.length) await amendCooperation(coopId, ops, scopeParty)
      // `exclusive || loadedForm`: a fresh form's unticked box is a default, not a
      // revocation — on the existed-fallthrough it may only GRANT exclusivity.
      // ⚠️ 1075 — THE TWO AGENCY-LEVEL FLAGS GO IN ONE AMEND, and that is a correctness
      // requirement now, not a round-trip saving. Owner ruling 2026-09-12: exclusivity means
      // the full right to advertise and sell, "do not share" means no right to share at all,
      // so the pair is a contradiction and the database refuses to store it
      // (`cooperation_exclusive_xor_no_republish`). Sent as two amends, switching from one to
      // the other would set the second while the first was still true and hit the constraint;
      // in ONE op-list they land in a single UPDATE and the intermediate state never exists.
      const sharedOps = []
      let sentExclusive = false, sentNoRepublish = false, sentNoCooperation = false
      if (exclusive !== !!origRow?.exclusive && (exclusive || loadedForm)) {
        sentExclusive = true
        sharedOps.push({ op: 'set', field: 'exclusive', value: exclusive })
        // credit the agent whose scope is active when exclusivity is granted; clear on revoke
        sharedOps.push(exclusive && scopeParty
          ? { op: 'set', field: 'exclusive_held_by_party_id', value: scopeParty }
          : { op: 'clear', field: 'exclusive_held_by_party_id' })
      }
      // 1073 — `no_republish` amends the PARENT with no participant scope, exactly like
      // exclusivity: it is the owner's instruction about their advert, not one agent's term.
      // ⚠️ Same `|| loadedForm` guard, and for the same reason: a fresh form's unticked box
      // is a DEFAULT, not the owner saying "you may share". On the existed-fallthrough it may
      // only ever SET the refusal — never clear one somebody else recorded from the advert.
      // The column is nullable on purpose (never-asked ≠ allowed), so an untouched form must
      // leave it null rather than writing false over an unread question.
      if (noRepublish !== !!origRow?.no_republish && (noRepublish || loadedForm)) {
        sentNoRepublish = true
        sharedOps.push({ op: 'set', field: 'no_republish', value: noRepublish })
      }
      // 1080 — same door, same guard, no exclusion: „does not cooperate" stands beside
      // „do not share" rather than against it.
      if (noCooperation !== !!origRow?.no_cooperation && (noCooperation || loadedForm)) {
        sentNoCooperation = true
        sharedOps.push({ op: 'set', field: 'no_cooperation', value: noCooperation })
      }
      if (sharedOps.length) {
        await amendCooperation(coopId, sharedOps)
        setOrig(o => o ? { ...o,
          ...(sentExclusive ? { exclusive } : {}),
          ...(sentNoRepublish ? { no_republish: noRepublish } : {}),
          ...(sentNoCooperation ? { no_cooperation: noCooperation } : {}) } : o)
      }
      // B115 v2: the reason rides along and becomes `cooperation.ended_reason`, which
      // 0176's trigger reads to decide archive vs 7 days vs 90 days + owner cooldown.
      // The reason becomes `cooperation.ended_reason`, which 0178's trigger reads to
      // decide archive vs deprioritise vs debounce. The free-text note rides in the
      // same field for 'unknown', because that is the only case where prose is the
      // point — a bare 'unknown' teaches nobody anything.
      // `reason:detail` — the detail is the owner's stated fact (an exclusivity end
      // date) or the mandatory prose behind "Other". 0179's trigger splits on ':'.
      if (loadedForm && status !== origRow?.status) {
        let reason = endReason || null
        if (endReason === 'unknown' && endNote.trim()) reason = `unknown:${endNote.trim()}`
        if (endReason === 'owner_exclusive_elsewhere' && exclUntil) reason = `owner_exclusive_elsewhere:${exclUntil}`
        await setCooperationStatus(coopId, status, reason)
      }
      // Saved → the draft's reason to exist is gone; the new baseline is what we just
      // wrote, so a silent save leaves the form CLEAN (dirty=false) without a refetch.
      try { localStorage.removeItem(draftKey) } catch {}
      setDraftRestored(false)
      if (silent) {
        baseRef.current = { ...fieldsNow, priceInherited }
        // keep the diff baselines in step too, or the NEXT save would re-send these ops
        const savedVals = {
          commission_percent: terms.commission_percent, min_price: terms.min_price,
          min_price_currency: (terms.min_price != null || before?.min_price != null) ? currency : (before?.min_price_currency ?? null),
          valid_until: terms.valid_until, notes: notes || null,
          terms: { ...(before?.terms || {}), ...conditions },
        }
        if (scopeParty) setOrigP(p => ({ ...(p || {}), ...savedVals }))
        else setOrig(o => ({ ...(o || {}), ...savedVals }))
        setOrig(o => o ? { ...o, exclusive, no_republish: noRepublish, no_cooperation: noCooperation } : o)
        setOrig(o => o ? { ...o, status } : o)
        if (automatic) { failedAutoSaveRef.current = null; setAutoSaveState('saved') }
        onSaved?.()
        return
      }
      onSaved?.()
      onClose()
    } catch (e) {
      // The guard above catches what THIS form typed, but a percent can also arrive from
      // elsewhere (a seeded value, another modal's op-list). Those still come back as the raw
      // Postgres text — translate it instead of showing the agent a constraint name. Rethrow
      // the readable version so the reshare that aborts says why it aborted.
      const err = /cooperation_participant_commission_percent_check/.test(e?.message || '')
        ? new Error(t('desk_coop_pct_out_of_range'))
        : e
      setErr(err.message)
      if (automatic) { failedAutoSaveRef.current = autoSaveSignature; setAutoSaveState('error') }
      if (silent) throw err
    } finally { setSaving(false) }
  }

  // Cooperations save themselves after the first meaningful edit. Text and numeric inputs wait briefly so typing
  // one value creates one event instead of an amendment per keystroke; toggles/selects use
  // the same short delay so every control has one predictable persistence rule. An untouched
  // NEW modal remains clean and creates nothing: merely looking is not relationship evidence.
  // Failed autosaves leave the form
  // dirty, so the next edit retries and the visible Retry control can retry immediately.
  useEffect(() => {
    // ⚠️ `manualSave` FIRST: a host that opted out must never reach setAutoSaveState either,
    // or the footer would announce "saving…" for a save that is never going to happen.
    if (manualSave) return
    if (loading || readOnly || !dirty || saving || failedAutoSaveRef.current === autoSaveSignature) return
    setAutoSaveState('pending')
    const timer = setTimeout(() => {
      handleSave({ silent: true, automatic: true }).catch(() => {})
    }, 700)
    return () => clearTimeout(timer)
  }, [id, loading, readOnly, manualSave, dirty, saving, commissionType, commissionPct, minPrice,
    ownerNetAmount, netCommissionAmount, currency, exclusive, noRepublish, noCooperation, validUntil, notes, status, autoSaveSignature])

  // ── CLOSING NEVER DROPS AN EDIT (2026-10-08) ─────────────────────────────────────────────
  // Owner report: "when agent saves comment on cooperation details it doesn't get saved — it
  // says it does but it doesn't." Measured on cooperation e3d686a0 (2026-10-08): two agents,
  // terms saved, notes NULL on every row, and NO amendment event at all after the open/join —
  // the note never left the browser. Their rhythm explains it: they open, type the fee (the
  // amend lands 1-2 s after the open), type the note LAST and press Close. The autosave above
  // waits 700 ms; closing unmounts the form, the cleanup clears the timer, and the edit is
  // gone. The footer still read "Saved automatically" from the earlier save. The browser
  // draft does not rescue it either: a form opened from a seed keys its draft on
  // `new:<property>:<contact>:<intent>`, and the next open (now with a cooperationId) looks
  // under the id key.
  // So on unmount -- every way out: Close, the modal's X, Escape, the card closing -- a dirty,
  // savable form saves NOW with the latest values. The request finishes after unmount (the
  // promise is not tied to the component); onSaved still reaches the host, which refreshes
  // its chips. A save already in flight does not block this one: it carries the values from
  // before the last keystrokes, and re-sending an unchanged field is a no-op amend.
  const flushRef = useRef(null)
  flushRef.current = { dirty, readOnly, manualSave, loading, save: handleSave }
  useEffect(() => () => {
    const f = flushRef.current
    if (!f || f.manualSave || f.readOnly || f.loading || !f.dirty) return
    f.save({ silent: true, automatic: true }).catch(() => {})
  }, [])

  // switch the form to THIS agent's own terms (0122)
  function selectParticipant(p) {
    setEditingPartyId(p.party_id); setOrigP(p)
    // Same inheritance rule as the load path: a stored price wins, an empty one inherits —
    // ⚠️ AND WITH THE SAME GUARD THE LOAD PATH CARRIES (added 2026-08-15). That guard exists
    // because seeding the scraped ad price "puts an unowned number in the box that reads
    // exactly like a colleague's agreed price", and it matters more since inherited values
    // save without a confirm step. This function is precisely how a manager scopes ONTO a
    // colleague's row, so it is the one place that must not do it: the diff then wrote the
    // ad's asking price to that colleague's `min_price`, and their negotiation showed a
    // price they never agreed. Only the viewer's OWN row may inherit.
    const inheritPrice = p.min_price == null && seedPrice != null && !!p.is_self
    const base = {
      commissionType: p.terms?.commission_type || 'percent',
      commissionPct: p.commission_percent ?? '',
      minPrice: inheritPrice ? String(seedPrice) : (p.min_price ?? ''),
      ownerNetAmount: p.terms?.owner_net_amount ?? '', netCommissionAmount: p.terms?.net_commission_amount ?? '',
      currency: p.min_price_currency || (inheritPrice && seedCurrency) || 'USD',
      exclusive: baseRef.current.exclusive,
      noRepublish: baseRef.current.noRepublish, noCooperation: baseRef.current.noCooperation,
      validUntil: p.valid_until || '', notes: p.notes || '',
      priceInherited: inheritPrice,
    }
    // Field-by-field rather than applyFields(): `exclusive` is AGENCY-level, not part of
    // a participant's row, so switching scope must not silently undo a toggle.
    setCommissionType(base.commissionType); setCommissionPct(base.commissionPct)
    setMinPrice(base.minPrice); setPriceInherited(base.priceInherited)
    setOwnerNetAmount(base.ownerNetAmount); setNetCommissionAmount(base.netCommissionAmount)
    setCurrency(base.currency); setValidUntil(base.validUntil); setNotes(base.notes)
    baseRef.current = base   // switching scope moves the dirty baseline with it
    setDraftRestored(false)
  }

  // Back to OWN-terms scope for a viewer with no participant row yet — a manager who
  // clicked ✎ onto a colleague's row would otherwise have no way to return to recording
  // their own (the row to select does not exist until they save). Blank form, same
  // inheritance rule as the load path's `joining` branch; save then joins-on-save.
  function selectSelfScope() {
    setEditingPartyId(null); setOrigP(null)
    const inheritPrice = seedPrice != null
    const base = {
      commissionType: seedType, commissionPct: seedCommission != null ? String(seedCommission) : '',
      minPrice: inheritPrice ? String(seedPrice) : '',
      ownerNetAmount: '', netCommissionAmount: '',
      currency: (inheritPrice && seedCurrency) || 'USD',
      exclusive: baseRef.current.exclusive,
      noRepublish: baseRef.current.noRepublish, noCooperation: baseRef.current.noCooperation,
      validUntil: '', notes: '', priceInherited: inheritPrice,
    }
    setCommissionType(base.commissionType); setCommissionPct(base.commissionPct)
    setMinPrice(base.minPrice); setPriceInherited(base.priceInherited)
    setOwnerNetAmount(base.ownerNetAmount); setNetCommissionAmount(base.netCommissionAmount)
    setCurrency(base.currency); setValidUntil(base.validUntil); setNotes(base.notes)
    baseRef.current = base
    setDraftRestored(false)
  }

  // Takes a ROSTER agent now, not a typed uuid (owner 2026-08-15). The scheduler agent id
  // goes to the server, which owns the 0040 agent→party bridge and refuses an unbridged
  // agent rather than minting an identity — the same contract the meeting's attendance and
  // client-representative paths already use.
  async function handleAddParticipant(agent) {
    if (!id || !agent?.id) return
    setSaving(true); setErr(null)
    try {
      // commission_share deliberately NOT sent (owner ruling 2026-07-29): before a
      // transaction exists there is no commission to split, so recording one during
      // negotiation is speculative and conflates two different lifecycle stages —
      // negotiating WITH THE OWNER vs distributing OUR fee internally. It also read as
      // a second "%" next to commission_percent on the same row, which is what made the
      // participants list look like a pre-agreed payout ledger.
      await updateCooperationParticipants(id, [{ op: 'add', scheduler_agent_id: agent.id }])
      const d = await getCooperation(id)
      setParticipants(d.participants || [])
      setVis(d.visibility || null)
    } catch (e) { setErr(e.message) } finally { setSaving(false) }
  }
  async function handleRemoveParticipant(partyId) {
    if (!id) return
    setSaving(true); setErr(null)
    try {
      await updateCooperationParticipants(id, [{ op: 'remove', party_id: partyId }])
      const d = await getCooperation(id)
      setParticipants(d.participants || [])
      setVis(d.visibility || null)   // leaving changes what you may do next — re-read it
    } catch (e) { setErr(e.message) } finally { setSaving(false) }
  }

  // inline (2026-07-29): hosted as a tab in LineageModal's combined deal editor
  // (agents fill cooperation + rental terms in one sitting) — body only, host owns
  // the chrome. Default keeps the standalone Modal for every other caller.
  const coopBody = (
    <>
      {loading ? (
        <p className="text-sm text-muted-foreground py-6 text-center">{t('desk_loading')}</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {/* Loaded row beats "unnamed" (2026-07-29): the existing-cooperation entry
                points pass no labels, but the fetched row carries the contact's name
                (projected — name is an agency fact) — use it before giving up. */}
            <span><Icon name="house" /> {propertyLabel || t('desk_coop_unnamed_property')}</span>
            <span><Icon name="arrow-left-right" /></span>
            <span><Icon name="user-check" /> {contactName || orig?.contact?.name || t('desk_coop_unnamed_contact')}</span>
          </div>

          {/* Say it FIRST, above the form, not after a failed save. `reason` comes from the
              projection so the sentence names the action that unlocks the form rather than
              just reporting a denial — 'not_participant' is fixable by the agent, so it gets
              an instruction; anything else is not, so it gets a plain statement. */}
          {(readOnly || joinsOnSave) && (
            <p className="text-sm rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-700 dark:text-amber-400">
              <Icon name="lock" />{' '}
              {joinsOnSave ? t('desk_coop_save_joins') : t('desk_coop_view_only')}
            </p>
          )}

          {/* Unsaved edits carried over from a previous open of this form (localStorage). */}
          {draftRestored && (
            <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-2">
              {t('desk_draft_restored')}
              <button type="button" className="underline" onClick={discardDraft}>{t('desk_draft_discard')}</button>
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">{t('desk_coop_intent_label')}</label>
              {/* Once the cooperation EXISTS the intent is frozen — it is part of the
                  identity triple (0046: property + contact + intent), so changing it would
                  mean a different cooperation, not an edited one. It was already
                  `disabled`, but a greyed <select> with a chevron still reads as "you may
                  change this, but not now / not you" — a false affordance the owner
                  flagged (2026-07-29: "why agent can select it"). A plain value reads as
                  what it is: settled. Same rule as the status field beside it. */}
              {id ? (
                <div className="px-3 py-2 text-sm">
                  {intent === 'sale' ? t('desk_coop_intent_sale')
                    : intent === 'girao' ? t('desk_listing_intent_girao')
                    : t('desk_coop_intent_rent')}
                </div>
              ) : (
              <select className={input} value={intent} onChange={e => { setIntent(e.target.value); setCommissionType('percent') }}>
                <option value="sale">{t('desk_coop_intent_sale')}</option>
                <option value="rent">{t('desk_coop_intent_rent')}</option>
                {/* 0219 widened cooperation_transaction_intent_check to accept girao.
                    Until it did, an agent working a გირავდება mandate had to file it as
                    rent or sale — which is how a rent cooperation ended up hanging off a
                    girao ad and broke the terms panel. */}
                <option value="girao">{t('desk_listing_intent_girao')}</option>
              </select>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">{t('desk_coop_status_label')}</label>
              {/* No manual dropdown (2026-07-11, agent-directed) — status should reflect what
                  actually happened on a meeting, not a guess typed in ahead of it. Until meeting
                  outcomes are wired to auto-transition this, it's read-only here; agents can still
                  end a dead cooperation manually below. */}
              <div className="px-3 py-2 text-sm">{t(COOP_STATUS_KEYS[status]) || status}</div>
            </div>
          </div>
          {/* B115 v3: ending asks WHY, because the reason — not a timer — decides what
              happens to the lead. RULING D: an agent must be able to close their own
              cooperation whether it SUCCEEDED or failed, so "we closed it" and "lost it to
              someone else's exclusive" are first-class options; without them an agency
              cannot tell a won deal from a lost one in its own data.
              Six rather than four now, which is against the "agent taps the first one"
              instinct — but the two additions describe outcomes an agent actively wants to
              record, not admin they want to skip. Anything unrecognised is `unknown`. */}
          {/* Ending is refused for a participant while ANYONE else is still working this
              cooperation (core_cooperation.js /status) — "step back" is the correct verb
              then, and it lives on your own participant row below. Hidden rather than
              shown-and-403'd. `!== false` so a server without visibility{} behaves as before. */}
          {/* ⚠️ NOT FROM A MEETING (added 2026-08-15). `scopeSelfOnly` is how the meeting
              modals mount this editor, and it was added to stop this panel reaching beyond
              the viewer's own participation — but it never touched the END door, so a drawer
              whose question is "what are the terms of THIS viewing" carried a control that
              ends the agency↔owner relationship for the whole property. In the CREATE modal
              it was reachable before the meeting existed, and cancelling the booking would
              not undo it. Ending a cooperation is a property-level act and belongs to the
              property's own surfaces, where the row being ended is the subject. */}
          {id && !scopeSelfOnly && status !== 'ended' && status !== 'rejected' && vis?.can_end !== false && (
            endReasonOpen ? (
              <div className="rounded border border-destructive/40 p-2">
                <p className="text-xs font-medium mb-1">{t('desk_coop_end_why')}</p>
                <div className="flex flex-col gap-1 items-start">
                  {/* ⚠️ `deal_done_by_us` is NOT offered here (0180). Closing our own deal
                      belongs to the Meetings flow, which carries the meeting, the
                      commission and the per-agent ranking credit — and it already ends
                      the cooperation as `deal_closed`. A free click in this dialog has
                      none of that behind it, so offering it would let unverified clicks
                      pollute the one metric the owner defined as verified ("they brought
                      commission to the company"). */}
                  {['deal_done_by_other', 'owner_exclusive_elsewhere',
                    'owner_declined_agent', 'cooperation_stale', 'unknown'].map(r => (
                    <button key={r} className="text-xs text-left hover:underline"
                      onClick={() => {
                        // "Other" is a data-quality black hole if it is frictionless:
                        // agents will feed it vague text and analytics will later learn
                        // nonsense from it with total confidence. So it — and ONLY it —
                        // demands a note before the end goes through.
                        if (r === 'unknown' && !(endNote || '').trim()) { setEndReason('unknown'); return }
                        // Exclusivity has a stated end — "until March" or "until sold".
                        // Ask, rather than model it: a date the OWNER gave is a fact we
                        // may act on; a duration we picked is a guess we may not.
                        // ⚠️ ALWAYS stop here, not only when endReason is still null
                        // (review fix 2026-07-23): picking "Other" first and then
                        // switching to exclusivity used to skip the date prompt entirely,
                        // so 0179 degraded the bare reason to until_sold — a
                        // never-auto-released archive — even when the owner stated a date.
                        if (r === 'owner_exclusive_elsewhere') { setEndReason(r); return }
                        setEndReason(r); setStatus('ended'); setEndReasonOpen(false)
                      }}>
                      • {t('desk_coop_end_' + r)}
                    </button>
                  ))}
                </div>
                {endReason === 'owner_exclusive_elsewhere' && (
                  <div className="mt-1">
                    <p className="text-xs text-muted-foreground">{t('desk_coop_excl_when')}</p>
                    <DateInput className={`flex items-center gap-0.5 ${input}`} value={exclUntil}
                      onChange={v => setExclUntil(v)} />
                    <div className="flex gap-2 mt-1">
                      <button className="text-xs disabled:opacity-50" disabled={!exclUntil}
                        onClick={() => { setStatus('ended'); setEndReasonOpen(false) }}>{t('save')}</button>
                      {/* ⚠️ "until sold" has no observable end — nothing will auto-release
                          it, and that is correct rather than a gap. A human learns it on
                          the next call. */}
                      <button className="text-xs" onClick={() => { setExclUntil('until_sold'); setStatus('ended'); setEndReasonOpen(false) }}>
                        {t('desk_coop_excl_until_sold')}
                      </button>
                    </div>
                  </div>
                )}
                {endReason === 'unknown' && (
                  <div className="mt-1">
                    <p className="text-xs text-amber-600">{t('desk_coop_end_note_required')}</p>
                    <input className={input} value={endNote} autoFocus
                      onChange={e => setEndNote(e.target.value)} />
                    <button className="text-xs mt-1 disabled:opacity-50" disabled={!endNote.trim()}
                      onClick={() => { setStatus('ended'); setEndReasonOpen(false) }}>{t('save')}</button>
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">{t('desk_coop_end_hint')}</p>
                <button className="text-xs mt-1" onClick={() => { setEndReasonOpen(false); setEndReason(null) }}>{t('cancel')}</button>
              </div>
            ) : (
              <button className="text-xs text-destructive" onClick={() => setEndReasonOpen(true)}>
                {t('desk_coop_status_ended')}
              </button>
            )
          )}

          <div className={card}>
            <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_coop_commission_section')}</p>
            {editingPartyId && (() => {
              const ep = participants.find(p => p.party_id === editingPartyId)
              // Your own row gets said as YOURS (owner 2026-07-29) — naming the viewer to
              // themselves ("editing Natali's terms" shown to Natali, or worse to a locked
              // non-participant) read as editing a colleague's negotiation.
              return (
                <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
                  {ep?.is_self
                    ? t('desk_coop_editing_terms_own')
                    : t('desk_coop_editing_terms_of').replace('{name}', ep?.party?.display_name || t('desk_coop_participant_hidden'))}
                  {/* manager peeking at a colleague's row: the way back to their OWN
                      (not-yet-existing) row is this button, not a row in the list */}
                  {notParticipant && (
                    <button type="button" className="underline ml-2" onClick={selectSelfScope}>
                      {t('desk_coop_record_own')}
                    </button>
                  )}
                </p>
              )
            })()}
            <div className="mb-3">
              <label className="text-xs text-muted-foreground">{t('desk_coop_commission_type_label')}</label>
              <select className={input} value={commissionType} disabled={readOnly} onChange={e => setCommissionType(e.target.value)}>
                <option value="percent">{t('desk_coop_type_percent')}</option>
                {/* ⚠️ EVERY INTENT, unlike `standard` (rent-only) and `net_amount` (sale-only).
                    „50%-ზე ვთანამშრომლობ" is said about lets and sales alike, and it was
                    being typed into the percent box, where 50 meant half the PRICE (owner
                    ruling 2026-09-12: "both, told apart by the commission type"). */}
                <option value="coop_split">{t('desk_coop_type_coop_split')}</option>
                {intent === 'sale' ? (
                  <>
                    <option value="net_amount">{t('desk_coop_type_net_amount')}</option>
                    <option value="net_commission">{t('desk_coop_type_net_commission')}</option>
                  </>
                ) : intent === 'girao' ? (
                  /* percent + fixed only. "standard" is defined as one month's RENT for an
                     annual lease (desk_coop_standard_note) and a girao has no monthly rent
                     at all — offering it here would let an agent pick a fee that cannot be
                     computed from anything in the deal (0219). */
                  <option value="fixed_net">{t('desk_coop_type_fixed_net')}</option>
                ) : (
                  <>
                    <option value="fixed_net">{t('desk_coop_type_fixed_net')}</option>
                    <option value="standard">{t('desk_coop_type_standard')}</option>
                  </>
                )}
              </select>
            </div>

            {commissionType === 'standard' ? (
              <p className="text-xs text-muted-foreground">{t('desk_coop_standard_note')}</p>
            ) : (
              <div className="flex flex-wrap gap-3 items-end">
                {/* WRAP, don't shrink (2026-08-01). This was `grid grid-cols-3`: three
                    rigid equal columns, where the price cell also has to hold a currency
                    select. Rendered INLINE inside the meeting modal (max-w-md) that leaves
                    the price input ~60px and "342" displays as "3" — a truncated PRICE,
                    the one number here nobody can afford to misread. Flex-wrap with
                    per-field minimums keeps the wide modal on one row and drops fields
                    onto their own line when the host is narrow, never clipping digits. */}
                <div className="flex-1 min-w-[10rem]">
                  <label className="text-xs text-muted-foreground">{t('desk_coop_negotiated_price')}</label>
                  <div className="flex gap-1">
                    {/* An inherited value is just THE VALUE (owner ruling 2026-08-01) — no
                        confirm step, no explanatory line, and styled like any other filled
                        field. Where it came from is a hover, not a paragraph: provenance is
                        worth answering when asked, not worth a line of screen every time. */}
                    <input className={`${input} min-w-0`}
                      inputMode="decimal" value={minPrice} disabled={readOnly}
                      title={priceInherited && seedPrice != null
                        ? t('desk_coop_price_from_ad_hover').replace('{p}', `${seedPrice} ${seedCurrency || 'USD'}`)
                        : undefined}
                      onChange={e => editPrice(e.target.value)} />
                    <select className="rounded-lg border border-input px-2 text-sm bg-background" value={currency} disabled={readOnly} onChange={e => changeCurrency(e.target.value)}>
                      <option>USD</option><option>GEL</option>
                    </select>
                  </div>
                </div>
                {(commissionType === 'percent' || commissionType === 'coop_split') && (
                  <div className="flex-1 min-w-[7rem]">
                    {/* The SAME stored field (commission_percent) under a different name,
                        because the number means a different thing. A box labelled
                        „საკომისიო %" holding 50 is the ambiguity this type removes; the
                        label has to move with the meaning or the type is decoration. */}
                    <label className="text-xs text-muted-foreground">
                      {t(commissionType === 'coop_split' ? 'desk_coop_split_pct_label' : 'desk_coop_commission_pct')}
                    </label>
                    {/* min/max mirror the DB check (0–100). They make the out-of-range value
                        visible AS IT IS TYPED; handleSave still validates, because these
                        attributes are advisory on a non-submitted input and say nothing at
                        all when a value is seeded rather than typed. */}
                    <input className={input} inputMode="decimal" type="number" min="0" max="100" step="0.1"
                           value={commissionPct} disabled={readOnly}
                           aria-invalid={commissionPct !== '' && !(Number(commissionPct) >= 0 && Number(commissionPct) <= 100)}
                           onChange={e => setCommissionPct(e.target.value)} />
                  </div>
                )}
                {/* SALE ONLY, and a question rather than a block (2026-09-16). On a let the percent is
                    a share of ONE MONTH'S RENT, so 50 and 100 are ordinary (1,432 of 1,435 rent rows
                    measured above 10). On a sale 2 of 61 were, and both read as the co-broke share
                    typed into the rate box — so the check offers the type that means that. */}
                {intent === 'sale' && commissionType === 'percent' && parseFloat(commissionPct) > 10 && (
                  <p className="w-full text-xs text-amber-600 dark:text-amber-400" data-coop-rate-check>
                    {t('desk_coop_rate_check').replace('{p}', commissionPct)}{' '}
                    {!readOnly && (
                      <button type="button" className="underline hover:no-underline" onClick={() => setCommissionType('coop_split')}>
                        {t('desk_coop_rate_check_is_split')}
                      </button>
                    )}
                  </p>
                )}
                {(commissionType === 'net_amount' || intent === 'sale') && (
                  <div className="flex-1 min-w-[7rem]">
                    <label className="text-xs text-muted-foreground">{t('desk_coop_owner_net_label')}</label>
                    <input className={input} inputMode="decimal" value={ownerNetAmount} disabled={readOnly} onChange={e => setOwnerNetAmount(e.target.value)} />
                  </div>
                )}
                {(commissionType === 'net_commission' || commissionType === 'fixed_net') && (
                  <div className="flex-1 min-w-[7rem]">
                    <label className="text-xs text-muted-foreground">{t('desk_coop_net_commission_label')}</label>
                    <input className={input} inputMode="decimal" value={netCommissionAmount} disabled={readOnly} onChange={e => setNetCommissionAmount(e.target.value)} />
                  </div>
                )}
                <div className="flex-1 min-w-[8rem]">
                  <label className="text-xs text-muted-foreground">{t('desk_coop_commission_amount')}</label>
                  <div className="px-3 py-2 text-sm text-muted-foreground italic">
                    {commissionAmount != null ? `${commissionAmount} ${currency}` : '—'}
                  </div>
                </div>
                {/* A dash beside a filled-in 50 reads as a broken calculator. Say which
                    number is missing instead — the fee itself, which this cooperation does
                    not record because the split is what was agreed. */}
                {commissionType === 'coop_split' && (
                  <p className="w-full text-xs text-muted-foreground">{t('desk_coop_split_note')}</p>
                )}
              </div>
            )}
          </div>

          {/* Pets / tenant preference removed 2026-07-11 as duplication — those are the
              owner's tenant-acceptance stances, already captured in the property's own
              RentalTermsModal (pol_animals chip + tenant_pref_notes), not a fact about
              the AGENT-TO-AGENT cooperation this modal records. */}
          <div className={card}>
            <label className="flex items-center gap-2 text-sm">
              {/* ⚠️ 1075 — TICKING ONE CLEARS THE OTHER (owner ruling 2026-09-12). Exclusivity is
                  the FULL right to advertise and sell this property; „არ გააზიაროთ" is NO right
                  to share it. They are opposite answers to the same question, so the form must
                  not let an agent assert both — and the database refuses the pair outright, so
                  a UI that allowed it would produce a save error instead of a record. 1081
                  extends the same rule to „არ თანამშრომლობს" (owner: "no — they contradict"),
                  so exclusivity clears BOTH refusals while they stay independent of each
                  other. Clearing
                  the other here rather than disabling it keeps one click enough to change your
                  mind; the save sends both in a single amend so the contradiction never exists
                  even for one statement. */}
              <input type="checkbox" checked={exclusive} disabled={readOnly}
                     onChange={e => { const v = e.target.checked; setExclusive(v); if (v) { setNoRepublish(false); setNoCooperation(false) } }} />
              {t('desk_coop_exclusive')}
              {/* exclusivity is agency-level, but an agent won it (0122) */}
              {orig?.exclusive_held_by_party_id && (
                <span className="text-xs text-muted-foreground">
                  {/* ⚠️ Never fall back to the raw uuid. The server (2026-08-19) nulls this
                      column when the holder's own participant row was withheld, so reaching
                      this branch with a value means we merely failed to match it — and an id
                      on screen is an identifier that resolves to a name elsewhere, the exact
                      thing the redaction strips. Name them, or say it is another agent. */}
                  · {t('desk_coop_exclusive_held_by')}: {participants.find(p => p.party_id === orig.exclusive_held_by_party_id)?.party?.display_name || t('desk_coop_participant_hidden')}
                </span>
              )}
            </label>
            {exclusive && (
              <div className="mt-3">
                <label className="text-xs font-medium text-muted-foreground">{t('desk_coop_valid_until')}</label>
                <DateInput className={`flex items-center gap-0.5 ${input}`} value={validUntil || ''} disabled={readOnly} onChange={v => setValidUntil(v)} />
              </div>
            )}
            {/* ── 1073: "do not share" (owner ruling 2026-09-12) ───────────────────────────
                Sits directly under exclusivity because it is the same KIND of fact — an
                agency-level condition the owner set, binding on everyone who works the flat
                — and because the advert itself publishes the two together: 20f16a13 reads
                "I cooperate with agencies at 1.5%, DO NOT COPY THE ADVERT". Measured in
                1,311 of 70,580 distinct advert texts, and until now it had nowhere to go.
                ⚠️ The box is a RECORD, not an enforcement. Nothing in the share, reshare or
                publish paths reads the column yet; making it block a button is a separate
                ruling, and a flag that silently disables a control is worse than none. */}
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={noRepublish} disabled={readOnly}
                     onChange={e => { const v = e.target.checked; setNoRepublish(v); if (v) setExclusive(false) }} />
              {t('desk_coop_no_republish')}
            </label>
            <p className="mt-1 ml-6 text-xs text-muted-foreground">{t('desk_coop_no_republish_hint')}</p>
            {/* ── 1080: „არ თანამშრომლობს" (owner ruling 2026-09-12) ────────────────────────
                NOT a third state of the same question. „არ გააზიაროთ" is about the ADVERT,
                this is about the RELATIONSHIP, and the owner was explicit that they can be
                published together („არ გააზიაროთ და არ ვთანამშრომლობ") or apart — so this
                box neither clears nor is cleared by the one above it. Only exclusivity
                contradicts, and only the advert box (1079). */}
            <label className="mt-3 flex items-center gap-2 text-sm">
              {/* 1081 — clears EXCLUSIVITY, never the box above it: an exclusive mandate is
                  the owner cooperating with us, so the two cannot both be true; „არ
                  გააზიაროთ" and „არ თანამშრომლობს" remain independent of each other. */}
              <input type="checkbox" checked={noCooperation} disabled={readOnly}
                     onChange={e => { const v = e.target.checked; setNoCooperation(v); if (v) setExclusive(false) }} />
              {t('desk_coop_no_cooperation')}
            </label>
            <p className="mt-1 ml-6 text-xs text-muted-foreground">{t('desk_coop_no_cooperation_hint')}</p>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">{t('desk_coop_notes')}</label>
            <textarea className={input} rows={2} value={notes} disabled={readOnly} onChange={e => setNotes(e.target.value)} />
          </div>

          {id && (
            <div className={card}>
              <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_coop_participants_section')}</p>
              <div className="space-y-1.5 mb-2">
                {participants.length === 0 && <p className="text-sm text-muted-foreground">{t('desk_coop_none_yet')}</p>}
                {participants.map(p => {
                  // Render ONLY what the server will accept (COOPERATION_VISIBILITY_SPEC §7).
                  // Before this, both buttons rendered on EVERY row: "remove" on someone
                  // else's row 403'd (loudly, after the click), and "terms ✎" on someone
                  // else's row SUCCEEDED — /amend had no authorization until 2026-07-29.
                  // A 403 is not a permission UI; it teaches the rule only after the agent
                  // has already tried to break it.
                  // `!== false` on the self branches = fall back to the old behaviour when
                  // the server predates visibility{}; the cross-agent branches require an
                  // explicit true, so an old server is never MORE permissive than today.
                  const canEditThis   = p.is_self ? vis?.can_amend_own_terms !== false : !!vis?.can_amend_others_terms
                  const canRemoveThis = p.is_self ? vis?.can_leave !== false           : !!vis?.can_manage_participants
                  return (
                  <div key={p.id} className={`flex items-center justify-between text-sm rounded px-1 ${editingPartyId === p.party_id ? 'bg-muted/60' : ''}`}>
                    <span>
                      {/* ⚠️ WITHHELD ≠ EMPTY (2026-08-15). Identity is now gated server-side
                          for a viewer with no right on this property, and the old fallback
                          chain (`display_name || party_id`) rendered a BLANK once both were
                          nulled — a row that says nothing reads as a bug, not as a rule.
                          `identity_state` is the server's own three-valued answer, so the
                          row says "another agent" and stays countable. */}
                      {p.identity_state === 'withheld'
                        ? <span className="text-muted-foreground italic">{t('desk_coop_participant_hidden')}</span>
                        : (p.party?.display_name || p.party_id)}
                      {p.is_self && <span className="text-muted-foreground"> · {t('desk_coop_you')}</span>}
                      {/* commission_share (the internal split) intentionally NOT rendered —
                          see handleAddParticipant. Two unrelated percentages on one row read
                          as a pre-agreed payout. What belongs here is this agent's OWN
                          negotiation with the owner (0122); disagreement between agents is
                          signal, shown side by side. */}
                      <span className="text-xs text-muted-foreground ml-1.5">
                        {/* THREE states, from the server (participant.terms_state) — the UI
                            cannot tell them apart itself, because redaction nulls the numbers
                            in place, making "withheld" byte-identical to "empty". Showing
                            both as "no terms yet" told an agent nobody had spoken to this
                            owner when a colleague already had — causing the duplicate work
                            this list exists to prevent. */}
                        {p.terms_state === 'withheld'
                          ? <span className="text-amber-600 dark:text-amber-400">· <Icon name="lock" /> {t('desk_coop_terms_withheld')}</span>
                          : (p.min_price != null || p.commission_percent != null)
                            ? `· ${p.min_price ?? '—'} ${p.min_price_currency || ''}${p.commission_percent != null ? ` · ${p.commission_percent}%` : ''}`
                            : `· ${t('desk_coop_no_terms')}`}
                      </span>
                    </span>
                    <span className="flex gap-2 shrink-0">
                      {canEditThis && (
                        <button className="text-xs text-primary" onClick={() => selectParticipant(p)}>{t('desk_coop_edit_terms')}</button>
                      )}
                      {canRemoveThis && (
                        // Your own row is "step back", not "delete": the cooperation
                        // continues without you and nothing is destroyed. Only management
                        // ever sees the word remove, on someone else's row.
                        <button className="text-xs text-destructive"
                          title={p.is_self ? t('desk_coop_leave_title') : undefined}
                          onClick={() => handleRemoveParticipant(p.party_id)}>
                          {p.is_self ? t('desk_coop_leave') : t('desk_coop_remove')}
                        </button>
                      )}
                    </span>
                  </div>
                  )
                })}
              </div>
              {/* The share input is GONE (owner ruling 2026-07-29): no commission exists to
                  split before a deal closes — see handleAddParticipant.
                  ── AND THE RAW AGENT-ID FIELD IS GONE TOO (owner ruling 2026-08-15) ──
                  It asked for a party UUID. Nobody has ever known one, none is displayed
                  anywhere in the Desk to copy, and the comment that used to sit here said
                  as much — it survived only because "how an agent joins" was an open
                  workflow question and deleting the field would have deleted the only way
                  to add anyone. The owner has now answered both halves: the roster picker,
                  and MANAGEMENT ONLY. `can_add_participant` is management-only server-side
                  now, so this control simply does not exist for an agent — and the write
                  refuses independently, because a hidden button is not a permission. */}
              {vis?.can_add_participant && (
                <AddParticipantPicker
                  taken={participants.map(p => p.party_id).filter(Boolean)}
                  busy={saving}
                  onPick={handleAddParticipant} />
              )}
            </div>
          )}

          {err && <p className="text-sm text-destructive">{err}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button className={btn('ghost')} onClick={onClose}>{t('desk_lineage_close_button')}</button>
            {/* No Save on a form that cannot be saved — the banner above already said why.
                A disabled Save would restate the denial without naming the way out. */}
            {/* A HOST THAT TURNED AUTOSAVE OFF MUST GET A REAL BUTTON. Leaving the status
                line here would say "saved" over a form nothing is saving — the worst of the
                two, because it reads as done. Disabled until there is something to save, so
                pressing it can never mint an empty cooperation out of merely looking, which
                is the guarantee the autosave path states in its own comment. */}
            {!readOnly && manualSave && (
              <button className={btn('primary')} disabled={saving || !dirty}
                onClick={() => handleSave().catch(() => {})}>
                {saving ? t('desk_coop_autosave_saving') : t('save')}
              </button>
            )}
            {!readOnly && !manualSave && (
              autoSaveState === 'error' ? (
                <button className={btn('primary')} disabled={saving}
                  onClick={() => { failedAutoSaveRef.current = null; handleSave({ silent: true, automatic: true }).catch(() => {}) }}>
                  {t('desk_coop_autosave_retry')}
                </button>
              ) : (
                <span className="inline-flex items-center px-3 text-sm text-muted-foreground" aria-live="polite">
                  {autoSaveState === 'saving' || autoSaveState === 'pending'
                    ? t('desk_coop_autosave_saving')
                    : id ? t('desk_coop_autosave_saved') : t('desk_coop_autosave_hint')}
                </span>
              )
            )}
          </div>
        </div>
      )}
    </>
  )
  if (inline) return coopBody
  return (
    <Modal title={t('desk_coop_modal_title')} onClose={onClose}>
      {coopBody}
    </Modal>
  )
}

export { CooperationModal }
